'use client';

import { Brain } from 'lucide-react';
import { useBrainChat } from './BrainChatProvider';
import { useBrainSessionStatus } from '../../lib/queries';
import { useTranslation } from '../../lib/i18n';
import { ReasoningScale } from '../../components/ui/ReasoningScale';
import { Toggle } from '../../components/ui/Toggle';
import { LoadingState, ErrorState, EmptyState } from '../../components/ui/states';
import { Button } from '../../components/ui/Button';
import type { useReasoningLevel } from './useReasoningLevel';

/** The body of the chat bar's reasoning popover — the counterpart of the CLI TUI's reasoning overlay, and
 *  it carries the same two things that command does: the effort levels the CURRENT model offers (applied
 *  live through POST /brain/think, exactly like the CLI) and the `show` sub-behaviour, which toggles
 *  the transcript's Thought rows. The levels come from the chat's own status row rather than the model
 *  catalog, so the panel reports what the running session actually supports.
 *
 *  The write also moves the account default, so an Account panel open elsewhere is refetched rather
 *  than left showing the value this panel just replaced.
 *
 *  The level write (`useReasoningLevel`) is owned by the host popover and passed in, so the host can refuse
 *  to close while a write is `saving` and the applied value survives the panel unmounting on close. */
export function ReasoningPanel({ level }: { level: ReturnType<typeof useReasoningLevel> }) {
  const { t } = useTranslation();
  const { activeSessionId, showThoughts, setShowThoughts } = useBrainChat();
  const statusQuery = useBrainSessionStatus(activeSessionId);
  const status = statusQuery.data;
  const levels = status?.thinkingLevels ?? [];
  const { applied, status: statusState, retryLevel, apply } = level;
  const current = applied ?? status?.thinkingLevel ?? '';

  return (
    <div className="flex flex-col gap-3" aria-busy={statusState === 'saving' || undefined}>
      {statusQuery.isLoading ? (
        <LoadingState variant="list" />
      ) : statusQuery.isError ? (
        <ErrorState message={t.common.daemonUnreachable} onRetry={() => statusQuery.refetch()} />
      ) : levels.length === 0 ? (
        <EmptyState title={t.reasoning.noLevelsTitle} description={t.reasoning.noLevelsDesc} icon={Brain} />
      ) : (
        <div className="flex flex-col gap-2 rounded-md border border-border bg-muted px-3 py-3">
          <span className="text-small text-muted-foreground">{t.reasoning.effortLabel}</span>
          <ReasoningScale
            options={levels.map((level) => ({ value: level, label: status?.thinkingLevelLabels?.[level] ?? level }))}
            value={current}
            onChange={(level) => { void apply(level, current); }}
            ariaLabel={t.reasoning.effortLabel}
            disabled={statusState === 'unconfirmed'}
          />
        </div>
      )}

      <div className="flex items-center justify-between gap-3 rounded-md border border-border bg-muted px-3 py-2">
        <span className="text-small text-muted-foreground">{t.reasoning.thoughtRows}</span>
        <Toggle checked={showThoughts} onChange={setShowThoughts} label={t.reasoning.thoughtRows} />
      </div>

      {/* The failure is reported once, by the level write's toast; this is only the way to try again. */}
      {statusState === 'error' && retryLevel ? (
        <div className="flex justify-end">
          <Button type="button" variant="ghost" onClick={() => { void apply(retryLevel, current); }}>{t.common.retry}</Button>
        </div>
      ) : null}
    </div>
  );
}
